import React, { useContext, useEffect, useState, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import EmptyView from "../../../../Component/EmptyView/EmptyView";
import FilterSelection from "../../common/FilterSelection";
import { ActionTable } from "./ActionTable";
import { useDispatch, useSelector } from "react-redux";
import {
  filterData,
  prepareActionsListData,
} from "../../../../utils/actions-helpers";
import {
  addActionsIntoList,
  getCustomerBrandProductCases,
  getSourcesWithAlias,
} from "../../../../services/apiService";
import { createDomainUrlMapping } from "./ActionModal";
import { fetchActionMetrics } from "../../../../redux/actions/metricActions";
import { AuthContext } from "../../../../context/auth-context";

export function prepareSelectOptions(dataArray) {
  const options = {
    llm: new Set(),
    persona: new Set(),
    market: new Set(),
    geo: new Set(),
    brand: new Set(),
    product: new Set(),
    category: new Set(),
  };

  dataArray?.forEach((item) => {
    if (item.llm) options.llm.add(item.llm);
    if (item.persona) options.persona.add(item.persona);
    if (item.category) options.category.add(item.category);
    if (item.market) options.market.add(item.market);
    if (item.geography) options.geo.add(item.geography);
    if (!item.isProduct && item.brand) options.brand.add(item.brand);
    if (item.isProduct && item.brand) options.product.add(item.brand);
  });

  return Object.fromEntries(
    Object.entries(options).map(([key, values]) => [
      key,
      [...values].map((value) => ({ value, label: value })),
    ])
  );
}

const updateRecommendedActionList = (data1, actionsListData) => {
  const existingActions = new Set(
    actionsListData?.map((mainItem) => {
      const item = mainItem?.action_json;
      return JSON.stringify({
        persona: item?.persona,
        geography: item?.geography,
        category: item?.category,
        market: item?.market,
        llm: item?.llm,
        brand: item?.brand,
        mainTitle: item?.title || item?.mainTitle,
        compTitles: item?.compTitles,
      });
    }) || []
  );

  return data1
    .map((obj) => {
      const filteredObj = { ...obj };
      ["Reach", "Sentiment", "Performance"].forEach((key) => {
        if (filteredObj[key]) {
          filteredObj[key] = filteredObj[key].filter((item) => {
            const itemKey = JSON.stringify({
              persona: obj.persona,
              geography: obj.geography,
              category: obj?.category,
              market: obj.market,
              llm: obj.llm,
              brand: obj.brand,
              mainTitle: item.mainTitle,
              compTitles: item.compTitles,
            });
            return !existingActions.has(itemKey);
          });
        }
      });

      return Object.values(filteredObj).some(
        (arr) => Array.isArray(arr) && arr.length
      )
        ? filteredObj
        : null;
    })
    .filter(Boolean);
};

const Actions = ({
  handleChange,
  isLoading,
  actionMetricsData,
  sourceAliasIds,
  domaUrlsMapping,
  actionableData,
}) => {
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const customerId = user?.customer_id;
  const [isActionsLoading, setIsActionsLoading] = useState(true);
  const actionsListData = useSelector(
    (state) => state.actionMetrics?.data?.actionList
  );

  const recommendedActions = useMemo(() => {
    return actionMetricsData && actionsListData
      ? updateRecommendedActionList(actionMetricsData, actionsListData)
      : null;
  }, [actionMetricsData, actionsListData]);

  const [data, setData] = useState([]);

  const [filters, setFilters] = useState({
    brand: [],
    product: [],
    llm: [],
    market: [],
    geo: [],
    persona: [],
    category: [],
  });

  const filteredData = useMemo(() => {
    const data = actionMetricsData;
    return data?.filter((item) => {
      const matches = (key) => {
        const filterKey = key === "geo" ? "geography" : key;
        return !filters[key].length || filters[key].includes(item[filterKey]);
      };

      const isBrandValid =
        filters.brand.length &&
        !item.isProduct &&
        filters.brand.includes(item.brand);
      const isProductValid =
        filters.product.length &&
        item.isProduct &&
        filters.product.includes(item.brand);

      return (
        (filters.brand.length || filters.product.length
          ? isBrandValid || isProductValid
          : true) &&
        matches("llm") &&
        matches("market") &&
        matches("geo") &&
        matches("category") &&
        matches("persona")
      );
    });
  }, [actionMetricsData, filters, actionableData]);

  useEffect(() => {
    if (!recommendedActions) return;

    if (recommendedActions.length) {
      addRecommendedActionsIntoList(recommendedActions);
    } else {
      setIsActionsLoading(false);
    }
  }, [recommendedActions]);

  const handleFilterChange = (key, selectedOptions) => {
    setFilters((prev) => ({
      ...prev,
      [key]: selectedOptions.map((option) => option.value),
    }));
  };

  const addRecommendedActionsIntoList = async (actionMetricsData) => {
    if (!actionMetricsData?.length) return;
    // setIsActionsLoading(true);
    const actionsList = await prepareActionsListData(
      actionMetricsData,
      getSourcesWithAlias,
      sourceAliasIds,
      createDomainUrlMapping
    );
    const payload = { actionsArray: actionsList };

    await addActionsIntoList(payload, customerId);
    dispatch(fetchActionMetrics(customerId)).then(() => {
      setIsActionsLoading(false);
    });
  };

  const optionsMapping = useMemo(
    () => prepareSelectOptions(actionMetricsData),
    [actionMetricsData]
  );

  useEffect(() => {
    if (actionMetricsData && actionMetricsData.length > 0) {
      const options = prepareSelectOptions(actionMetricsData);

      const defaultLlm = options.llm?.some((item) => item.value === "gpt-4o")
        ? ["gpt-4o"]
        : [options.llm?.[0]?.value || ""];

      const defaultGeo = options.geo?.some((item) => item.value === "USA")
        ? ["USA"]
        : [options.geo?.[0]?.value || ""];

      const defaultMarket = options.market?.some(
        (item) => item.value === "General Market"
      )
        ? ["General Market"]
        : [options.market?.[0]?.value || ""];

      const defaultPersona = options.persona?.some(
        (item) => item.value === "General Persona"
      )
        ? ["General Persona"]
        : [options.persona?.[0]?.value || ""];

      const defaultCategory = [options?.category?.[0]?.value || ""];

      setFilters({
        brand: [options?.brand?.[0]?.value || ""],
        product: [],
        llm: defaultLlm,
        market: defaultMarket,
        geo: defaultGeo,
        persona: defaultPersona,
        category: defaultCategory,
      });
    }
  }, [actionMetricsData]);

  const processedData = async (data) => {
    const { brand, product } = filters;
    const brandProductAssocitions = await getCustomerBrandProductCases(
      customerId,
      brand,
      product
    );

    return filterData(data, brandProductAssocitions);
  };

  useEffect(() => {
    async function getData() {
      setData([]);
      const data = await processedData(filteredData);
      setData(data);
    }
    getData();
  }, [filters]);

  return (
    <div className="col-md-12 mt-4">
      {isActionsLoading || isLoading || !recommendedActions ? (
        <div className="text-center mt-4">
          <Spinner
            animation="grow"
            size="sm"
            role="status"
            aria-hidden="true"
          />
          &nbsp; Loading...
        </div>
      ) : actionMetricsData.length === 0 ? (
        <EmptyView message="Nothing here" />
      ) : (
        <>
          <div className="row">
            {[
              "brand",
              "product",
              "category",
              "llm",
              "market",
              "geo",
              "persona",
            ].map((filterKey) => (
              <div
                style={{ width: "210px" }}
                className="col-md-1"
                key={filterKey}
                id={`${filterKey}_select`}  
              >
                <FilterSelection
                  options={optionsMapping[filterKey]}
                  type={filterKey}
                  selectedOptions={filters[filterKey].map((value) => ({
                    value,
                    label: value,
                  }))}
                  handleChange={(selectedOptions) =>
                    handleFilterChange(filterKey, selectedOptions)
                  }
                />
              </div>
            ))}
          </div>
          <br />
          <ActionTable
            handleChange={handleChange}
            tableType="opp"
            actionMetricsData={data}
            sourceAliasIds={sourceAliasIds}
            domaUrlsMapping={domaUrlsMapping}
          />
        </>
      )}
    </div>
  );
};

export default Actions;
